import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import BottomTabBar from './components/layout/BottomTabBar';
import HomeScreen from './features/home/HomeScreen';
import TimelineScreen from './features/timeline/TimelineScreen';
import BucketListScreen from './features/collection/BucketListScreen';
import CalendarScreen from './features/calendar/CalendarScreen';
import MoreScreen from './features/fun/MoreScreen';
import AuthScreen from './features/auth/AuthScreen';
import { AppProvider, useAppContext } from './context/AppContext';
import { Loader2, AlertTriangle, LogOut, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, loadingMessage, initError, signOut } = useAppContext();

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          className="mb-6"
        >
          <Loader2 className="text-primary" size={48} />
        </motion.div>
        <p className="text-sm font-bold text-charcoal/60 text-center animate-pulse">
          {loadingMessage}
        </p>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-8 text-center">
        <div className="bg-red-50 p-6 rounded-[32px] mb-8 shadow-sm border border-red-100">
          <AlertTriangle className="text-red-500 mx-auto mb-4" size={48} />
          <h2 className="text-xl font-black text-charcoal mb-2">초기화 오류 발생</h2>
          <p className="text-xs text-red-400 font-bold leading-relaxed max-w-[240px] mx-auto">
            {initError}
          </p>
        </div>
        
        <div className="flex flex-col w-full max-w-xs gap-3">
          <button 
            onClick={() => window.location.reload()}
            className="flex items-center justify-center gap-2 py-4 bg-primary text-white font-black rounded-2xl shadow-lg shadow-primary/20 active:scale-95 transition-all"
          >
            <RefreshCw size={18} /> 다시 시도
          </button>
          
          <button 
            onClick={() => signOut()}
            className="flex items-center justify-center gap-2 py-4 bg-white border border-secondary text-red-500 font-bold rounded-2xl active:scale-95 transition-all"
          >
            <LogOut size={18} /> 로그아웃 후 다시 로그인
          </button>
        </div>
        
        <p className="mt-8 text-[10px] text-gray-300 font-medium">
          오류가 지속되면 관리자에게 문의해 주세요.
        </p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

function AppContent() {
  const { user } = useAppContext();
  
  const [activeTab, setActiveTab] = useState('home');

  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        return <HomeScreen />;
      case 'calendar':
        return <CalendarScreen />;
      case 'timeline':
        return <TimelineScreen />;
      case 'bucket':
        return <BucketListScreen />;
      case 'more':
        return <MoreScreen />;
      default:
        return <HomeScreen />;
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <main className="max-w-lg mx-auto px-4 relative">
        {renderContent()}
      </main>
      {user && <BottomTabBar activeTab={activeTab} setActiveTab={setActiveTab} />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppProvider>
        <Routes>
          <Route path="/auth" element={<AuthScreen />} />
          <Route 
            path="/*" 
            element={
              <ProtectedRoute>
                <AppContent />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </AppProvider>
    </BrowserRouter>
  );
}

export default App;
